import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UiService } from '../../services/ui.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TranslateModule],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss'],
})
export class Sidebar implements OnInit, OnDestroy {
  currentLanguage: string = 'en';
  currentPageType: 'inbox' | 'sent' | 'completed' | 'myRequests' | null = null;
  private destroy$ = new Subject<void>();
  overlayActive = false;

  constructor(
    private router: Router,
    public uiService: UiService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.currentLanguage = this.translate.currentLang || this.translate.defaultLang || 'en';
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((evt) => {
        this.currentLanguage = evt.lang;
      });
    // Track current page type from route for sidebar nav item highlighting
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateCurrentPageType();
    });
    this.updateCurrentPageType();

    // Subscribe to sidebar open state to toggle mobile overlay
    this.uiService.sidebarOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOpen => {
        this.overlayActive = isOpen;
        // add/remove open class on root element for animation
        const el = document.querySelector('.sidebar');
        if (el) {
          if (isOpen) el.classList.add('open');
          else el.classList.remove('open');
        }
      });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Update current page type from route for nav item highlighting
   */
  private updateCurrentPageType(): void {
    const pageTypeMap: { [key: string]: 'inbox' | 'sent' | 'completed' | 'myRequests' } = {
      'inbox': 'inbox',
      'sent': 'sent',
      'completed': 'completed',
      'my-sent': 'myRequests'
    };

    const url = this.router.url;

    // Check if in task viewer (detail page) - extract pageType from route params
    if (url.includes('/task-view/')) {
      const match = url.match(/\/task-view\/(\d+)\//);
      if (match) {
        const pageTypeNum = parseInt(match[1], 10);
        const pageTypeReverseMap: { [key: number]: 'inbox' | 'sent' | 'completed' | 'myRequests' } = {
          0: 'inbox',
          1: 'sent',
          2: 'completed',
          3: 'myRequests'
        };
        this.currentPageType = pageTypeReverseMap[pageTypeNum] || null;
      }
    } else {
      // Check list pages
      for (const [path, pageType] of Object.entries(pageTypeMap)) {
        if (url.includes(`/requests/${path}`)) {
          this.currentPageType = pageType;
          return;
        }
      }
      this.currentPageType = null;
    }
  }

  /**
   * Check if nav item should be active based on current page type
   */
  isNavItemActive(pageName: string): boolean {
    const pageMap: { [key: string]: 'inbox' | 'sent' | 'completed' | 'myRequests' } = {
      'inbox': 'inbox',
      'sent': 'sent',
      'completed': 'completed',
      'my-sent': 'myRequests'
    };
    return this.currentPageType === pageMap[pageName];
  }
}
